import { useState, useEffect } from 'react';
import { Trash2, Eye, Check, Phone, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { getQuotes, markQuoteRead, deleteQuote } from '@/lib/api';

export const QuotesManager = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const fetchQuotes = async () => {
    try {
      const response = await getQuotes();
      setQuotes(response.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
  }, []);

  const handleView = async (quote) => {
    setSelectedQuote(quote);
    setViewDialogOpen(true);
    if (!quote.is_read) {
      try {
        await markQuoteRead(quote.id);
        fetchQuotes();
      } catch (error) {
        console.error('Error marking quote as read:', error);
      }
    }
  };

  const handleDelete = async () => {
    if (!selectedQuote) return;
    
    try {
      await deleteQuote(selectedQuote.id);
      toast.success('Zapytanie usunięte');
      setDeleteDialogOpen(false);
      setSelectedQuote(null);
      fetchQuotes();
    } catch (error) {
      toast.error('Wystąpił błąd');
    }
  };

  const openDeleteDialog = (quote, e) => {
    e.stopPropagation();
    setSelectedQuote(quote);
    setDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="quotes-manager">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Zapytania o wycenę</h1>
        <p className="text-slate-400">Przeglądaj zapytania od potencjalnych klientów</p>
      </div>

      <div className="bg-[#111827] rounded-xl border border-cyan-500/10 overflow-hidden">
        {quotes.length === 0 ? (
          <div className="px-6 py-12 text-center text-slate-400">
            Brak zapytań
          </div>
        ) : (
          <div className="divide-y divide-cyan-500/10">
            {quotes.map((quote) => (
              <div
                key={quote.id}
                onClick={() => handleView(quote)}
                className={`px-6 py-4 cursor-pointer hover:bg-cyan-500/5 transition-colors ${
                  !quote.is_read ? 'bg-cyan-500/5' : ''
                }`}
                data-testid={`quote-row-${quote.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      quote.is_read ? 'bg-slate-700' : 'bg-cyan-500/20'
                    }`}>
                      {quote.is_read ? (
                        <Check className="w-5 h-5 text-slate-400" />
                      ) : (
                        <Eye className="w-5 h-5 text-cyan-400" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-white">{quote.name}</p>
                        {!quote.is_read && (
                          <span className="px-2 py-0.5 bg-cyan-500 text-[#0A0F1C] text-xs font-medium rounded-full">
                            Nowe
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-1">
                        <span className="flex items-center gap-1 text-sm text-slate-400">
                          <Phone className="w-3 h-3" />
                          {quote.phone}
                        </span>
                        <span className="text-sm text-slate-400">{quote.service_type}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1 text-sm text-slate-500">
                      <Calendar className="w-4 h-4" />
                      {new Date(quote.created_at).toLocaleDateString('pl-PL')}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => openDeleteDialog(quote, e)}
                      className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                      data-testid={`delete-quote-${quote.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-lg bg-[#111827] border-cyan-500/20" data-testid="view-quote-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Szczegóły zapytania</DialogTitle>
            <DialogDescription className="text-slate-400">
              Informacje o zapytaniu klienta
            </DialogDescription>
          </DialogHeader>
          {selectedQuote && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-slate-500 block mb-1">
                    Imię i nazwisko
                  </label>
                  <p className="font-medium text-white">{selectedQuote.name}</p>
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-slate-500 block mb-1">
                    Telefon
                  </label>
                  <a 
                    href={`tel:${selectedQuote.phone}`}
                    className="font-medium text-cyan-400 hover:underline"
                  >
                    {selectedQuote.phone}
                  </a>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-slate-500 block mb-1">
                    Usługa
                  </label>
                  <p className="font-medium text-white">{selectedQuote.service_type}</p>
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-slate-500 block mb-1">
                    Data
                  </label>
                  <p className="font-medium text-white">
                    {new Date(selectedQuote.created_at).toLocaleString('pl-PL')}
                  </p>
                </div>
              </div>
              {selectedQuote.message && (
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-slate-500 block mb-1">
                    Wiadomość
                  </label>
                  <p className="text-slate-300 bg-[#0A0F1C] p-4 rounded-lg border border-cyan-500/10">
                    {selectedQuote.message}
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)} className="border-cyan-500/20 text-slate-300 hover:bg-cyan-500/10">
              Zamknij
            </Button>
            {selectedQuote && (
              <a href={`tel:${selectedQuote.phone}`}>
                <Button className="bg-gradient-to-r from-cyan-400 to-cyan-600 text-[#0A0F1C] hover:from-cyan-300 hover:to-cyan-500 font-semibold">
                  <Phone className="w-4 h-4 mr-2" />
                  Zadzwoń
                </Button>
              </a>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md bg-[#111827] border-cyan-500/20" data-testid="delete-quote-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Usuń zapytanie</DialogTitle>
            <DialogDescription className="text-slate-400">
              Ta operacja jest nieodwracalna.
            </DialogDescription>
          </DialogHeader>
          <p className="text-slate-400 py-4">
            Czy na pewno chcesz usunąć zapytanie od "{selectedQuote?.name}"? 
            Ta operacja jest nieodwracalna.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="border-cyan-500/20 text-slate-300 hover:bg-cyan-500/10">
              Anuluj
            </Button>
            <Button 
              onClick={handleDelete}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="confirm-delete-quote-button"
            >
              Usuń
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuotesManager;
